import { Callout } from "components/ui/callout";
import * as React from "react";

interface ActionBarChildrenParams {
    close: () => void;
}

interface IActionBarComponentProps {
    label: string;
    value: React.ReactNode;
    disabled?: boolean;
    disabledReason?: string;
    children?: ((params: ActionBarChildrenParams) => React.ReactNode) | React.ReactNode;
}

export const ActionBarComponent: React.FunctionComponent<IActionBarComponentProps> = (
    props: IActionBarComponentProps,
) => {
    const { label, value, disabled, disabledReason, children } = props;
    const [isCalloutOpen, setIsCalloutOpen] = React.useState(false);
    const openCallout = React.useCallback(() => {
        if (disabled) {
            return;
        }
        setIsCalloutOpen(true);
    }, [disabled, setIsCalloutOpen]);

    const renderButton = React.useCallback(() => {
        return (
            <div onClick={openCallout}>
                <button
                    type="button"
                    disabled={disabled}
                    title={disabled ? disabledReason : undefined}
                    className={`rounded-full px-3 py-1 flex items-center gap-2 shadow border text-sm font-medium transition ${
                        disabled
                            ? "bg-zinc-50 border-zinc-200 text-zinc-400 cursor-not-allowed"
                            : "bg-blue-50 border-zinc-200 text-zinc-800 hover:bg-blue-100"
                    }`}
                    aria-label={label}>
                    <span className="text-xs text-zinc-500">{label}</span>
                    <span className="font-semibold">{value}</span>
                </button>
            </div>
        );
    }, [label, value, disabled, disabledReason, openCallout]);

    const params = React.useMemo(() => {
        return {
            close: () => {
                setIsCalloutOpen(false);
            },
        };
    }, [setIsCalloutOpen]);

    return (
        <Callout
            renderButton={renderButton}
            onOpen={openCallout}
            forceClose={disabled || !isCalloutOpen}>
            <div className="w-80">
                {typeof children === "function" ? children(params) : children}
            </div>
        </Callout>
    );
};
